import React, { useEffect, useState } from 'react';
import { appointmentService } from '../../services/appointment.service';
import { AppointmentSettingsDto } from '../../types/appointment.types';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import { toastService } from '../../services/toast.service';

export const AppointmentSettings: React.FC = () => {
  const [settings, setSettings] = useState<AppointmentSettingsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await appointmentService.getAppointmentSettings();
        setSettings(data);
      } catch (err) {
        setError('Не вдалося завантажити налаштування прийомів.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSettingChange = (field: keyof AppointmentSettingsDto, value: string) => {
    if (settings) {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 0) {
        setSettings({ ...settings, [field]: numValue });
      }
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    setIsSaving(true);
    try {
      await appointmentService.updateAppointmentSettings(settings);
      toastService.success('Налаштування прийомів успішно збережено');
    } catch (err) {
      toastService.error('Не вдалося зберегти налаштування.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Налаштування прийомів
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {settings && (
        <Box component="form" noValidate autoComplete="off">
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Тривалість слоту (хв)"
                type="number"
                value={settings.slotDurationMinutes}
                onChange={(e) => handleSettingChange('slotDurationMinutes', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Буфер між прийомами (хв)"
                type="number"
                value={settings.bufferMinutes}
                onChange={(e) => handleSettingChange('bufferMinutes', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Запис наперед (днів)"
                type="number"
                value={settings.advanceBookingDays}
                onChange={(e) => handleSettingChange('advanceBookingDays', e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      )}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave} disabled={isSaving || !settings}>
          {isSaving ? <CircularProgress size={24} /> : 'Зберегти налаштування'}
        </Button>
      </Box>
    </Paper>
  );
};
